import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, effect, inject, input, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ProductDetailOverlayComponent } from 'src/app/category/components/product-detail-overlay/product-detail-overlay.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { ProductListing, ProductListingVariant } from 'src/app/products/models/product-listing.model';
import { Product, ProductDealCountdown, Variant } from 'src/app/products/models/product.model';
import { DealCountdownService, RemainingTime } from 'src/app/products/services/deal-countdown.service';
import { PriceService } from 'src/app/products/services/price.service';
import { CashpointsPipe } from 'src/app/shared/pipes/cashpoints.pipe';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { CashPointSvgComponent } from "../cash-point-svg/cash-point-svg.component";
import { VariantViewerComponent } from '../variant-viewer/variant-viewer.component';

export interface ColorVariant {
  color: string;
  image: string;
  url_path: string;
  product_id: string;
  in_stock: boolean;
}

enum VariantAttribute {
  Color = 'Color'
}

@Component({
  standalone: true,
  selector: 'base-product-list-card',
  templateUrl: './product-list-card.component.html',
  styleUrls: ['./product-list-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DealCountdownService],
  imports: [
    CommonModule,
    RouterModule,
    VariantViewerComponent,
    CashPointSvgComponent,
    CashpointsPipe,
    IndianCurrencyPipe
  ]
})
export class ProductListCardComponent implements OnInit {
  private priceService = inject(PriceService);
  router = inject(Router);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private dealCountdownService = inject(DealCountdownService);
  protected overlayService = inject(OverlayService);
  private browserApiService = inject(BrowserApiService);

  IMAGE_URLS = IMAGE_URLS;

  // Signal-based properties
  private _sellingPrice = signal<number>(0);
  private _percentSavings = signal<number>(0);
  private _savings = signal<number>(0);
  private _actualPrice = signal<number>(0);
  private _product = signal<ProductListing>({} as ProductListing);
  private _remainingTime = signal<RemainingTime | null>(null);
  private _storeConfiguration = signal<any>(null);

  // Computed signals
  readonly sellingPrice = this._sellingPrice.asReadonly();
  readonly percentSavings = this._percentSavings.asReadonly();
  readonly savings = this._savings.asReadonly();
  readonly actualPrice = this._actualPrice.asReadonly();
  readonly product = this._product.asReadonly();
  readonly remainingTime = this._remainingTime.asReadonly();
  readonly storeConfiguration = this._storeConfiguration.asReadonly();

  endDay: Date = new Date();
  isDealAvailable = false;
  productDealCountDown!: ProductDealCountdown;

  colorVariants: ColorVariant[] = [];

  previewImage = '';
  showImages = false;
  productVariant!: ProductListingVariant;



  readonly productInput = input<ProductListing>({} as ProductListing);

  constructor() {
    effect(() => {
      const productValue = this.productInput();
      if (productValue) {
        let processedProduct = productValue;

        if (productValue?.variants.length > 0) {
          const firstVariant = productValue.variants[0];
          if (firstVariant) {
            this.productVariant = firstVariant;
          }
          const allProductVariants = [...productValue?.variants ?? []];

          this.colorVariants = this.getColorVariants(allProductVariants);
          const priceAvailableInAllVariants = allProductVariants?.every(variant => variant?.hasOwnProperty('mrp'));

          if (priceAvailableInAllVariants && allProductVariants.length > 0) {
            const variant_group = productValue?.variant_group;
            const variantsForSale = allProductVariants.filter(variant => variant?.is_active && variant?.is_visible);
            const lowestPriceVariant = variantsForSale[0];
            processedProduct = { ...lowestPriceVariant, name: lowestPriceVariant?.display_name, featured_images: [{ url: lowestPriceVariant?.featured_images?.[0]?.url || '' }], variant_group } as unknown as ProductListing;
          }
        }

        this._product.set(processedProduct);
        const { actualPrice, sellingPrice } = this.priceService.getProductListingPrice(this.productVariant);
        this._sellingPrice.set(sellingPrice);
        this._actualPrice.set(actualPrice);
        this.setDefaultImage();
      }
    });
  }

  ngOnInit(): void {
    if (this.isDealAvailable) {
      this.endDay = new Date(this.productDealCountDown.end_time);
      this.dealCountdownService.getCountDown(this.endDay);

      // Set up effect to watch for remaining time changes
      effect(() => {
        const remainingTime = this.dealCountdownService.remainingTime();
        this._remainingTime.set(remainingTime);
      });
    }

    // Set up effect to watch for store configuration changes
    effect(() => {
      const storeConfig = this.storeConfigurationRepositoryService.storeConfigurations();
      this._storeConfiguration.set(storeConfig);
    });
  }

  private getColorVariants(variants: ProductListingVariant[]): ColorVariant[] {
    const variantsForSale = variants.filter(variant => variant?.is_active && variant?.is_visible);

    const hasColorsForAll = variantsForSale.every(variant => Array.isArray(variant?.variant_values) && variant.variant_values.some(attribute => attribute.key === VariantAttribute.Color));
    if (!hasColorsForAll) {
      return [];
    }

    if (Array.isArray(variantsForSale)) {
      const colorVariants = variantsForSale
        .map(variant => {
          const color = variant.variant_values.find(attribute => attribute.key === VariantAttribute.Color)?.value;
          return ({
            color,
            image: variant.featured_images[0]?.url,
            url_path: variant.url_path,
            product_id: variant.id,
            in_stock: variant?.in_stock
          });
        })
        .filter(variant => variant.color !== undefined)
        .sort((v1, v2) => {
          if (v1.color === v2.color) {
            return v1.in_stock ? -1 : 1;
          }
          return (v1.color || '') > (v2.color || '') ? 1 : -1;
        });

      const uniqueColors = [...new Set(colorVariants.map(colorVariant => colorVariant.color))];

      const uniqueColorVariants = uniqueColors
        .map(color => colorVariants.find(variant => variant.color === color))
        .filter((variant): variant is ColorVariant => variant !== undefined);

      return uniqueColorVariants;
    }
    return [];
  }

  setPreviewImage(imageUrl: string) {
    this.previewImage = imageUrl;
  }

  clearPreviewImage() {
    this.setDefaultImage();
  }

  private setDefaultImage() {
    const imageUrl = this.productVariant?.featured_images?.[0]?.url;
    this.previewImage = imageUrl || '';
  }

  openProductDetailOverlay() {
    this.overlayService.open(ProductDetailOverlayComponent, this.productVariant.url_path, true, 'centered', 'product-detail-overlay', 'block-scroll');
  }

  navigateToProductDetailPage(route: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    this.browserApiService.open(url, '_blank');
  }

  onMouseEnter() {
    this.showImages = true;
  }

  onMouseLeave() {
    this.showImages = false;
  }
}

/**
 * to normalize the interface of the variants and the current product variant detail
 * @param product product detail received in the list
 * @returns variant object of the current product
 */
export function getCurrentProductVariantDetail(product: Product): Variant {

  const {
    can_order, is_active, is_allow_back_orders, is_archived, is_disabled, is_visible, product_id, id,
    sales_channel, url_path, featured_images, variant_values, in_stock, is_exposed, display_name
  } = product || {};

  return ({
    can_order, is_active, is_allow_back_orders, is_archived, is_disabled, is_visible,
    product_id: product_id ?? id,
    sales_channel, url_path,
    featured_images: featured_images,
    variant_values: variant_values, in_stock, display_name,
    is_exposed
  });
}



